import React from "react";
import SectionTitle from "../components/SectionTitle";
import { useLang } from "../context/LangContext";
import { PARTNER_TARGETS, BRAND_INFO } from "../lib/content";
import { ExternalLink } from "lucide-react";

export default function Partners() {
  const { lang, t } = useLang();
  const network = [
    "MedMaiora Medical Center",
    "Riviera Health & Wellness",
    "BeLonG Group",
  ];

  return (
    <main className="pt-32" data-testid="page-partners">
      <section className="container-luxe py-16">
        <SectionTitle
          eyebrow="PARTNERS · SAISON 2026"
          title={lang === "fr" ? "Un réseau Riviera Shockwaves trié sur le volet." : "A handpicked Riviera Shockwaves network."}
          subtitle={lang === "fr" ? "Riviera Shockwaves intervient à bord des yachts, en villa privée et lors d'événements privés — Monaco, Cala del Forte, Cannes, Saint-Tropez. Aucune logistique de votre côté." : "Riviera Shockwaves operates on board yachts, in private villas and at private events — Monaco, Cala del Forte, Cannes, Saint-Tropez. Zero logistics on your side."}
        />
      </section>

      {/* MedMaiora featured partner */}
      <section className="bg-[var(--surface-2)] hairline-b hairline-t" data-testid="partner-medmaiora">
        <div className="container-luxe py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">{lang === "fr" ? "PARTENAIRE MÉDICAL DE RÉFÉRENCE" : "FLAGSHIP MEDICAL PARTNER"}</div>
            <h3 className="font-display text-3xl lg:text-4xl text-[var(--ink)] font-light leading-tight">
              Cala del Forte · MedMaiora
            </h3>
            <div className="mt-3 font-serif-luxe italic text-lg text-[var(--ink)]/70">
              BeLonG Group · Riviera Wellness Center
            </div>
            <a href={BRAND_INFO.medmaiora_url} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-display text-[var(--gold)] hover:underline" data-testid="medmaiora-link">
              www.rivierawellnesscenter.com <ExternalLink size={13} />
            </a>
            <a href="tel:+3901841968078" className="mt-4 inline-flex items-center gap-2 text-[13px] font-display text-[var(--ink)]/80 hover:text-[var(--gold)] transition" data-testid="medmaiora-phone">
              +39 0184 196 80 78
            </a>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[15px] text-[var(--ink)]/85 leading-relaxed font-body">
              {lang === "fr"
                ? "Le Riviera Health and Wellness Center incarne un nouveau paradigme en médecine préventive et bien-être. Au sein du BeLonG Group, il associe une clinique boutique à un wellness center pour proposer des programmes personnalisés de longévité et de bien-être, fondés sur les dernières avancées en génétique et épigénétique."
                : "The Riviera Health and Wellness Center represents a new paradigm in preventive medicine and wellness. As part of the BeLonG Group, it integrates a boutique clinic with a nearby wellness center to offer personalised longevity and wellness programs based on the latest advances in genetics and epigenetics."}
            </p>
            <p className="mt-4 text-[15px] text-[var(--text-secondary)] leading-relaxed font-body">
              {lang === "fr"
                ? "Le MedMaiora Medical Center est une destination de confiance pour les services de santé et de bien-être : médecine générale ouverte à tous, massages, kinésithérapie, ostéopathie."
                : "The MedMaiora Medical Center is a trusted destination for healthcare and wellness services — daily general practice open to all patients, alongside massage therapy, physiotherapy, and osteopathy treatments."}
            </p>
            <p className="mt-4 text-[15px] text-[var(--text-secondary)] leading-relaxed font-body">
              {lang === "fr"
                ? "Le nouveau Riviera Health and Wellness Center y ajoute des programmes de régénération cellulaire, des traitements de la douleur et des expériences premium de bien-être, sous la supervision d'une équipe médicale dédiée."
                : "The new Riviera Health and Wellness Center adds cellular regeneration programs, pain management treatments and premium wellness experiences under the supervision of a dedicated medical team."}
            </p>
            <p className="mt-6 font-serif-luxe italic text-lg text-[var(--ink)] leading-snug">
              {lang === "fr"
                ? "« Notre vision : inverser le vieillissement biologique, optimiser la vitalité et amplifier un bien-être holistique. »"
                : "\"Our vision: to reverse biological ageing, optimise vitality, and enhance holistic wellbeing.\""}
            </p>
          </div>
        </div>
      </section>

      {/* Brand network */}
      <section className="container-luxe pb-16">
        <div className="overline mb-6">{lang === "fr" ? "Marques associées" : "Associated brands"}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <a href={BRAND_INFO.exomobil_url} target="_blank" rel="noopener noreferrer" className="beam-card p-8 group" data-testid="brand-exomobil">
            <div className="flex items-center justify-between">
              <span className="overline">{t("stats.mobility")}</span>
              <ExternalLink size={16} className="text-[var(--ink)]/40 group-hover:text-[var(--gold)] transition" />
            </div>
            <div className="mt-6 flex items-center bg-[var(--surface-2)] hairline rounded-md p-6 min-h-[120px]">
              <img src="/logos/exomobil.png" alt="Exomobil" className="h-16 w-auto logo-light" />
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-5">{lang === "fr" ? "Unité mobile premium · exosquelette · intervention discrète à bord et à domicile." : "Premium mobile unit · exoskeleton · discreet on-board & home intervention."}</p>
            <span className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-[var(--gold)] font-display">www.exomobil.com</span>
          </a>
          <a href={BRAND_INFO.xp_therapy_url} target="_blank" rel="noopener noreferrer" className="beam-card p-8 group" data-testid="brand-xp">
            <div className="flex items-center justify-between">
              <span className="overline">Tech</span>
              <ExternalLink size={16} className="text-[var(--ink)]/40 group-hover:text-[var(--gold)] transition" />
            </div>
            <div className="mt-6 flex items-center bg-[var(--surface-2)] hairline rounded-md p-6 min-h-[120px]">
              <img src="/logos/xp-therapy.png" alt="XP Therapy" className="h-14 w-auto logo-light" />
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-5">{lang === "fr" ? "Plateforme instrumentée · monitoring temps réel · protocoles cliniques certifiés." : "Instrumented platform · real-time monitoring · certified clinical protocols."}</p>
            <span className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-[var(--gold)] font-display">xp-therapy.com</span>
          </a>
        </div>
      </section>

      {/* Ciblage partenaires */}
      <section className="bg-surface-1 hairline-t hairline-b py-20">
        <div className="container-luxe">
          <SectionTitle
            eyebrow="CIBLAGE PARTENAIRES"
            title={lang === "fr" ? "Nos relais privilégiés." : "Our preferred relays."}
            subtitle={lang === "fr" ? "Nous travaillons en confiance avec quatre familles d'acteurs Riviera. Programme partenaire détaillé sur demande, confidentiel." : "We work in trust with four Riviera stakeholder families. Detailed partner program on request, confidential."}
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-px hairline">
            {PARTNER_TARGETS.map((p) => (
              <div key={p.num} className="bg-base p-7" data-testid={`partner-target-${p.num}`}>
                <div className="font-display text-3xl text-[var(--gold)] font-light">{p.num}</div>
                <p className="mt-6 font-display text-base text-[var(--ink)] font-light leading-snug">{lang === "fr" ? p.title_fr : p.title_en}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-[var(--ink)]/40 font-display tracking-[0.15em] uppercase">
            {lang === "fr" ? "Programme commercial confidentiel · Sur demande à contact@riviera-shockwaves.com" : "Confidential commercial program · On request to contact@riviera-shockwaves.com"}
          </p>
        </div>
      </section>

      {/* Network */}
      <section className="container-luxe py-20" data-testid="partner-network">
        <div className="overline mb-6">{t("stats.network")}</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px hairline">
          {network.map((p) => (
            <div key={p} className="bg-surface-1 p-7 flex items-center justify-center min-h-[120px]">
              <span className="font-serif-luxe italic text-base md:text-lg text-[var(--ink)]/80 text-center">{p}</span>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
